import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Download, FileText, Calendar, MapPin, Mail, Globe, Phone, Award, Briefcase, GraduationCap, BrainCircuit, Code } from 'lucide-react';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

// Styled Components
const ResumeContainer = styled.div`
  max-width: 1000px;
  margin: 6rem auto 4rem;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;
  animation: ${fadeIn} 0.8s ease-out forwards;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
    margin-top: 5rem;
  }
`;

const DownloadSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 2rem 0;
  padding: 0 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0;
  }
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${({ theme }) => theme.colors.accent};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.accent}dd;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const ResumeHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  align-items: start;
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 1.5rem;
  }
`;

const HeaderContent = styled.div`
  h1 {
    font-size: 2.5rem;
    color: ${({ theme }) => theme.colors.accent};
    margin-bottom: 0.5rem;
    line-height: 1.2;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      font-size: 2rem;
    }
  }

  .header-title {
    font-size: 1.8rem;
    color: ${({ theme }) => theme.colors.accent};
    margin-bottom: 2rem;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      font-size: 1.5rem;
      margin-bottom: 1.5rem;
    }
  }
`;

const ContactInfo = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    color: ${({ theme }) => theme.colors.accent};
    flex-shrink: 0;
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  max-width: 300px;
  border-radius: 12px;
  border: 4px solid ${({ theme }) => theme.colors.secondary};
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 200px;
    margin: 0 auto;
    display: block;
  }
`;

const Section = styled.div`
  margin-bottom: 2.5rem;
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  box-shadow: ${({ theme }) => theme.shadows.md};
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.2s;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.5rem;
    color: ${({ theme }) => theme.colors.accent};
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    svg {
      color: ${({ theme }) => theme.colors.accent};
    }
    
    &::after {
      content: '';
      flex: 1;
      height: 2px;
      background: ${({ theme }) => theme.colors.secondary};
      margin-left: 0.75rem;
    }
  }
`;

const ExperienceItem = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
  animation: ${slideInRight} 0.5s ease-out forwards;
  animation-delay: ${({ $index }) => `${0.1 * ($index + 1)}s`};
  opacity: 0;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  .date {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    
    svg {
      color: ${({ theme }) => theme.colors.accent};
    }
  }

  .company {
    font-weight: bold;
    margin: 0.5rem 0;
    font-size: 1.1rem;
  }

  .position {
    font-style: italic;
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  ul {
    list-style-type: none;
    padding-left: 1.5rem;
  }

  li {
    position: relative;
    padding-left: 1rem;
    margin-bottom: 0.5rem;

    &::before {
      content: "•";
      position: absolute;
      left: 0;
      color: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr 1fr;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const SkillCategory = styled.div`
  animation: ${slideInRight} 0.5s ease-out forwards;
  animation-delay: ${({ $index }) => `${0.1 * ($index + 1)}s`};
  opacity: 0;

  .skill-title {
    font-weight: bold;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.text};
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    svg {
      color: ${({ theme }) => theme.colors.accent};
    }
  }

  ul {
    list-style-type: none;
    padding-left: 1rem;
  }

  li {
    position: relative;
    padding-left: 1rem;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.textSecondary};

    &::before {
      content: "•";
      position: absolute;
      left: 0;
      color: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const SkillItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;

  .skill-name {
    width: 200px;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const SkillLevel = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const Dot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ theme, filled }) => 
    filled ? theme.colors.accent : theme.colors.secondary};
  transition: background-color 0.3s ease;
`;

const EducationItem = styled(ExperienceItem)`
  .institution {
    font-weight: bold;
    margin: 0.5rem 0;
    font-size: 1.1rem;
  }
  
  .degree {
    font-style: italic;
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ResumeFooter = styled.div`
  margin-top: 3rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const Resume = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/resume');
    }
  }, [isAuthenticated, loading, navigate]);

const handleDownload = () => {
  // Ensure we construct a URL that uses the same protocol (HTTPS) as the current page
  const baseUrl = window.location.origin; // This includes the current protocol, domain and port
  const downloadUrl = `${baseUrl}/private/serve_resume.php`;
  
  // Navigate to the URL
  window.location.href = downloadUrl;
};

  if (loading || !isAuthenticated) {
    return null;
  }

  return (
    <ResumeContainer>
      <DownloadSection>
        <DownloadButton onClick={handleDownload}>
          <Download size={20} />
          Download PDF
        </DownloadButton>
      </DownloadSection>

      <ResumeHeader>
        <HeaderContent>
          <h1>LEBENSLAUF</h1>
          <div className="header-title">PASCAL DIETRICH</div>
          <ContactInfo>
            <ContactItem>
              <Calendar size={18} />
              <span>09.07.2000</span>
            </ContactItem>
            <ContactItem>
              <MapPin size={18} />
              <span>Dresden, Sachsen</span>
            </ContactItem>
            <ContactItem>
              <Mail size={18} />
              <span>mail@pascalndietrich.de</span>
            </ContactItem>
            <ContactItem>
              <Globe size={18} />
              <span>https://github.com/PascalND</span>
            </ContactItem>
          </ContactInfo>
        </HeaderContent>
        <ProfileImage src="/images/profilepic.jpg" alt="Pascal Dietrich" />
      </ResumeHeader>

      <Section>
        <h2><Briefcase size={20} /> BERUFSERFAHRUNG</h2>
        <ExperienceItem $index={0}>
          <div className="date">
            <Calendar size={16} />
            09/2021 - 08/2025
          </div>
          <div className="company">MAGIX Software GmbH, Dresden Sachsen</div>
          <div className="position">Ausbildung als Fachinformatiker für Anwendungsentwicklung</div>
          <ul>
            <li>Entwicklung nachhaltiger C++-Softwarelösungen nach Clean-Code-Prinzipien</li>
            <li>Konzeption modularer Systemarchitekturen mittels Flow-Design-Methodik</li>
            <li>Implementierung und Optimierung automatisierter CI/CD-Prozesse</li>
          </ul>
        </ExperienceItem>

        <ExperienceItem $index={1}>
          <div className="date">
            <Calendar size={16} />
            seit 04/2024
          </div>
          <div className="company">MEDIA Logistik GmbH</div>
          <div className="position">Datenerfasser</div>
          <ul>
            <li>Systematische Durchführung von Datenabgleichen und -validierungen</li>
          </ul>
        </ExperienceItem>

        <ExperienceItem $index={2}>
          <div className="date">
            <Calendar size={16} />
            11/2020 - 08/2021
          </div>
          <div className="company">Puritas Gesundheitshaus, Dresden Sachsen</div>
          <div className="position">Callcenter Agent in Teilzeit</div>
          <ul>
            <li>Analyse von Kundenbedürfnissen und Entwicklung von Lösungsvorschlägen</li>
          </ul>
        </ExperienceItem>
      </Section>

      <Section>
        <h2><GraduationCap size={20} /> AUSBILDUNG</h2>
        <EducationItem $index={0}>
          <div className="date">
            <Calendar size={16} />
            09/2021 - 08/2025
          </div>
          <div className="institution">MAGIX Software GmbH / BSZ für Technik "Gustav Anton Zeuner"</div>
          <div className="degree">Ausbildung zum Fachinformatiker für Anwendungsentwicklung</div>
          <ul>
            <li>Schwerpunkt: Entwicklung von Desktop-Anwendungen in C++</li>
            <li>Erlernen von Konzepten der Softwarearchitektur und Clean-Code-Prinzipien</li>
            <li>Umsetzung realer Projekte im Entwicklungsalltag</li>
          </ul>
        </EducationItem>
        
        <EducationItem $index={1}>
          <div className="date">
            <Calendar size={16} />
            08/2017 - 07/2019
          </div>
          <div className="institution">BSZ für Technik "Gustav Anton Zeuner", Dresden</div>
          <div className="degree">Fachhochschulreife, Technische Ausrichtung</div>
          <ul>
            <li>Abschluss mit Fachhochschulreife im technischen Bereich</li>
            <li>Schwerpunkte: Informatik, Mathematik und Physik</li>
          </ul>
        </EducationItem>
      </Section>

      <Section>
        <h2><BrainCircuit size={20} /> FÄHIGKEITEN UND KENNTNISSE</h2>
        <SkillsGrid>
          <SkillCategory $index={0}>
            <div className="skill-title">
              <Globe size={16} />
              Fremdsprachen
            </div>
            <SkillItem>
              <span className="skill-name">Deutsch</span>
              <SkillLevel>
                {[...Array(6)].map((_, i) => (
                  <Dot key={i} filled />
                ))}
              </SkillLevel>
            </SkillItem>
            <SkillItem>
              <span className="skill-name">Englisch</span>
              <SkillLevel>
                {[...Array(6)].map((_, i) => (
                  <Dot key={i} filled={i < 5} />
                ))}
              </SkillLevel>
            </SkillItem>
          </SkillCategory>

          <SkillCategory $index={1}>
            <div className="skill-title">
              <Code size={16} />
              Programmiersprachen
            </div>
            <SkillItem>
              <span className="skill-name">C++</span>
              <SkillLevel>
                {[...Array(6)].map((_, i) => (
                  <Dot key={i} filled />
                ))}
              </SkillLevel>
            </SkillItem>
            <SkillItem>
              <span className="skill-name">Python</span>
              <SkillLevel>
                {[...Array(6)].map((_, i) => (
                  <Dot key={i} filled={i < 4} />
                ))}
              </SkillLevel>
            </SkillItem>
            <SkillItem>
              <span className="skill-name">C#</span>
              <SkillLevel>
                {[...Array(6)].map((_, i) => (
                  <Dot key={i} filled={i < 5} />
                ))}
              </SkillLevel>
            </SkillItem>
            <SkillItem>
              <span className="skill-name">HTML, Javascript, CSS, PHP</span>
              <SkillLevel>
                {[...Array(6)].map((_, i) => (
                  <Dot key={i} filled={i < 3} />
                ))}
              </SkillLevel>
            </SkillItem>
            <SkillItem>
              <span className="skill-name">SQL</span>
              <SkillLevel>
                {[...Array(6)].map((_, i) => (
                  <Dot key={i} filled />
                ))}
              </SkillLevel>
            </SkillItem>
          </SkillCategory>

          <SkillCategory $index={2}>
            <div className="skill-title">
              <FileText size={16} />
              Software-Kenntnisse
            </div>
            <ul>
              <li>MS-Office: sehr gut</li>
              <li>Visual Studio: sehr gut</li>
              <li>Premiere Pro, Vegas Pro: gut</li>
              <li>CMake: Grundlagen</li>
            </ul>
          </SkillCategory>

          <SkillCategory $index={3}>
            <div className="skill-title">
              <Award size={16} />
              Softskills
            </div>
            <ul>
              <li>Kommunikativ, Charismatisch, Kritikfähig</li>
              <li>Flexibel, Lernbereit, Teamfähig</li>
              <li>Analytisches Denken, Problemlösungsfähigkeit</li>
              <li>Selbstständiges Arbeiten, Eigeninitiative</li>
            </ul>
          </SkillCategory>
        </SkillsGrid>
      </Section>
      
      <Section>
        <h2><Award size={20} /> PROJEKTE UND ZUSATZQUALIFIKATIONEN</h2>
        <ExperienceItem $index={0}>
          <div className="company">Clean-Code-Entwicklung</div>
          <div className="position">Fortlaufende Weiterbildung</div>
          <ul>
            <li>Anwendung von Clean-Code-Prinzipien in der täglichen Entwicklungsarbeit</li>
            <li>Implementierung von Test-Driven-Development (TDD) mit GoogleTest und GoogleMock</li>
            <li>Fortlaufende Optimierung von Legacy-Code durch Refactoring-Techniken</li>
          </ul>
        </ExperienceItem>
        
        <ExperienceItem $index={1}>
          <div className="company">Moderne C++-Techniken</div>
          <div className="position">Selbststudium</div>
          <ul>
            <li>Vertiefung in C++11/14/17/20-Features</li>
            <li>Anwendung von Design Patterns unter Berücksichtigung moderner C++-Paradigmen</li>
            <li>Leistungsoptimierung und Memory Management in C++</li>
          </ul>
        </ExperienceItem>
      </Section>
      
      <ResumeFooter>
        <p>Datum: 28. Februar 2025 | Pascal Dietrich</p>
      </ResumeFooter>
    </ResumeContainer>
  );
};

export default Resume;